import React from 'react';
import './AboutPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toSentenceCase } from '../utils/textUtils';
import useSEO from '../hooks/useSEO';

function AboutPage() {
  useSEO({
    title: "À Propos | Histoire, Mission & Valeurs Éducatives",
    description: "Découvrez le Groupe Scolaire et Universitaire Les Palmiers à Togba (Abomey-Calavi) : son histoire, ses valeurs d'excellence, son encadrement pédagogique rigoureux et ses infrastructures.",
    keywords: "À propos GSU Les Palmiers, histoire école Les Palmiers, vision pédagogique Bénin, collège lycée Togba, mission éducative Abomey-Calavi",
    canonicalUrl: "https://gsulespalmiers.bj/about"
  });

  return (
    <div className="about-page">
      <Header activePage="about" />
      
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{toSentenceCase("Qui sommes-nous ?")}</h1>
            <p className="hero-description">
              {toSentenceCase("Le Groupe Scolaire et Universitaire Les Palmiers, reconnu par l'État béninois, forme depuis plusieurs années des apprenants compétents dans l'enseignement général, technique, professionnel et supérieur.")}
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Valeurs */}
      <section className="mvv-section">
        <div className="container">
          <div className="mvv-grid">
            <div className="mvv-card mission">
              <div className="mvv-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2>{toSentenceCase("Notre Mission")}</h2>
              <p>{toSentenceCase("Former une jeunesse qualifiée et citoyenne, capable de contribuer activement au développement du Bénin.")}</p>
            </div>

            <div className="mvv-card vision">
              <div className="mvv-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2>{toSentenceCase("Notre Vision")}</h2>
              <p>{toSentenceCase("Devenir une référence en éducation et formation au Bénin et en Afrique de l'Ouest.")}</p>
            </div>

            <div className="mvv-card values">
              <div className="mvv-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2>{toSentenceCase("Nos Valeurs")}</h2>
              <div className="values-list">
                <span className="value-item">Excellence</span>
                <span className="value-item">Rigueur</span>
                <span className="value-item">Professionnalisme</span>
                <span className="value-item">Innovation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Message du Fondateur */}
      <section className="founder-section">
        <div className="container">
          <div className="founder-content">
            <div className="founder-header">
              <h2>{toSentenceCase("Message du Fondateur")}</h2>
              <p className="founder-intro">{toSentenceCase("Chers parents, chers élèves, chers partenaires,")}</p>
            </div>
            
            <div className="founder-message">
              <div className="founder-image-container">
                <img 
                  src="/images/fondateur.jpg" 
                  alt="Dr AHOSSIN Elvis Franck M. - Fondateur du GSU Les Palmiers" 
                  className="founder-image"
                />
                <div className="founder-signature">
                  <div className="signature-card">
                    <h4>{toSentenceCase("Le Fondateur")}</h4>
                    <p className="founder-name">Dr AHOSSIN Elvis Franck M.</p>
                  </div>
                </div>
              </div>
              
              <div className="message-text">
                <p>
                  {toSentenceCase("Depuis sa création, le Groupe Scolaire et Universitaire Les Palmiers s'est donné pour mission de former des générations de jeunes capables de relever les défis d'un monde en constante évolution. Nous croyons que l'éducation n'est pas seulement l'acquisition de connaissances, mais aussi la transmission de valeurs solides, le développement des talents et la préparation à la vie citoyenne et professionnelle.")}
                </p>
                
                <p>
                  {toSentenceCase("C'est dans cette vision que nous avons structuré nos pôles de formation — du préscolaire au secondaire technique et professionnel — afin d'offrir à chaque apprenant un parcours éducatif complet, moderne et adapté aux besoins de la société.")}
                </p>
                
                <p>
                  {toSentenceCase("Aux parents qui nous confient leurs enfants, je voudrais exprimer toute ma gratitude pour la confiance renouvelée. Aux élèves, je rappelle que leur avenir se construit aujourd'hui, et que chaque effort compte.")}
                </p>
                
                <p className="founder-conclusion">
                  {toSentenceCase("Ensemble, continuons à bâtir un avenir meilleur, solide et prometteur, sous l'ombre protectrice des Palmiers.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Équipe dirigeante */}
      <section className="team-section">
        <div className="container">
          <div className="team-header">
            <h2>{toSentenceCase("Notre équipe dirigeante")}</h2>
            <p>{toSentenceCase("Des professionnels dévoués au service de l'excellence éducative")}</p>
          </div>
          
          <div className="team-grid">
            {/* Assistante de Direction */}
            <div className="team-member-card">
              <div className="member-card-inner">
                <div className="member-card-front">
                  <div className="member-image-wrapper">
                    <img src="/images/secretaire.jpg" alt="Assistante de Direction" className="member-photo" />
                  </div>
                  <div className="member-quick-info">
                    <h3 className="member-title">{toSentenceCase("HOUNKANRIN Bénédicta")}</h3>
                    <p className="member-subtitle">{toSentenceCase("Assistante de Direction")}</p>
                    <div className="member-contact-info">
                      <div className="contact-item">
                        <span className="contact-icon">📞</span>
                        <span>01 99 64 99 34</span>
                      </div>
                      <div className="contact-item">
                        <span className="contact-icon">📞</span>
                        <span>01 48 25 83 20</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Directrice Administrative et Financière */}
            <div className="team-member-card">
              <div className="member-card-inner">
                <div className="member-card-front">
                  <div className="member-image-wrapper">
                    <img src="/images/directrice.jpg" alt="Directrice Administrative et Financière" className="member-photo" />
                  </div>
                  <div className="member-quick-info">
                    <h3 className="member-title">{toSentenceCase("AZIGUINON Megnissé Brigitte")}</h3>
                    <p className="member-subtitle">{toSentenceCase("Directrice Administrative et Financière")}</p>
                    <div className="member-contact-info">
                      <div className="contact-item">
                        <span className="contact-icon">📞</span>
                        <span>01 62 00 39 80</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="testimonials-section">
        <div className="container">
          <div className="testimonials-header">
            <h2>{toSentenceCase("Témoignages")}</h2>
            <p>{toSentenceCase("Ce que disent nos élèves, parents et partenaires")}</p>
          </div>

          {/* Témoignages Élèves */}
          <div className="testimonial-category">
            <h3>{toSentenceCase("Témoignages élèves")}</h3>
            <div className="testimonials-grid">
              <div className="testimonial-card student">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">E</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Élève en 2nde G2 (IMI)")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Ce que j'aime au GSU Les Palmiers, c'est l'équilibre entre la rigueur des cours et les activités pratiques. Dans ma filière, nous utilisons de vrais équipements informatiques et cela me prépare déjà au monde professionnel.")}"
                </p>
              </div>

              <div className="testimonial-card student">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">E</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Élève en Terminale (MMV)")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Grâce aux ateliers de la filière Mode et Métiers du Vêtement, j'ai pu réaliser mes propres créations. Je rêve de devenir styliste, et ici je me sens encouragée et encadrée pour y arriver.")}"
                </p>
              </div>
            </div>
          </div>

          {/* Témoignages Parents */}
          <div className="testimonial-category">
            <h3>{toSentenceCase("Témoignages parents")}</h3>
            <div className="testimonials-grid">
              <div className="testimonial-card parent">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">M</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Maman d'une élève en 5ème")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Ce que j'apprécie, c'est le suivi. Les enseignants communiquent régulièrement avec nous et nous sentons que nos enfants sont pris en main. L'éducation va au-delà des cours, ils apprennent aussi le respect et la discipline.")}"
                </p>
              </div>

              <div className="testimonial-card parent">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">P</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Papa d'un élève en 1ère (HR)")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Mon fils voulait faire carrière dans l'hôtellerie-restauration. Ici, il a accès à une formation pratique et à des stages qui l'aident à s'orienter. C'est une vraie chance.")}"
                </p>
              </div>
            </div>
          </div>

          {/* Témoignages Partenaires */}
          <div className="testimonial-category">
            <h3>{toSentenceCase("Témoignages partenaires")}</h3>
            <div className="testimonials-grid">
              <div className="testimonial-card partner">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">E</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Entreprise locale (Stage en génie civil)")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Nous accueillons régulièrement des stagiaires du GSU Les Palmiers. Leur sérieux et leur préparation témoignent du bon travail réalisé dans cet établissement.")}"
                </p>
              </div>

              <div className="testimonial-card partner">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">A</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Ancien élève en Informatique (IMI)")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Après mon Bac au GSU Les Palmiers, j'ai poursuivi mes études en génie logiciel. Les bases acquises ici, surtout en pratique, m'ont permis de m'intégrer rapidement et d'avoir confiance en moi.")}"
                </p>
              </div>
            </div>
          </div>

          {/* Témoignages Boursiers */}
          <div className="testimonial-category">
            <h3>{toSentenceCase("Témoignages boursiers")}</h3>
            <div className="testimonials-grid">
              <div className="testimonial-card scholarship">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">B</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Élève bénéficiaire d'une bourse scolaire")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("Sans la bourse, je n'aurais pas pu continuer mes études. Le soutien du lycée m'a donné une nouvelle chance, et aujourd'hui je travaille dur pour honorer cette confiance.")}"
                </p>
              </div>

              <div className="testimonial-card scholarship">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">P</div>
                  <div className="testimonial-info">
                    <h4>{toSentenceCase("Parent d'un(e) élève boursier(ère)")}</h4>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{toSentenceCase("La scolarisation gratuite des filles est une bénédiction. Ma fille va à l'école sans que je m'inquiète des frais. Merci à l'établissement et aux partenaires pour cette opportunité.")}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default AboutPage;
